(() => {
  function initializeHelpTooltip(help) {
    const trigger = help.querySelector('button');
    const tooltip = help.querySelector('[role="tooltip"]');

    function show() {
      document.querySelectorAll('[data-nested-product-help] [role="tooltip"]').forEach((other) => {
        other.hidden = other !== tooltip;
      });
      tooltip.style.removeProperty('--tooltip-left');
      const bounds = tooltip.getBoundingClientRect();
      const shift = Math.max(0, 16 - bounds.left) - Math.max(0, bounds.right - document.documentElement.clientWidth + 16);
      tooltip.style.setProperty('--tooltip-left', `calc(50% + ${shift}px)`);
    }

    help.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'touch') show();
    });
    help.addEventListener('pointerleave', () => {
      if (document.activeElement !== trigger) tooltip.hidden = true;
    });
    trigger.addEventListener('focus', show);
    trigger.addEventListener('blur', () => { tooltip.hidden = true; });
    trigger.addEventListener('click', show);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-nested-product-help] [role="tooltip"]').forEach((tooltip) => { tooltip.hidden = true; });
    }
  });
  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll('[data-nested-product-help]').forEach((help) => {
      if (!help.contains(event.target)) help.querySelector('[role="tooltip"]').hidden = true;
    });
  });

  function initializeNestedProduct(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.querySelectorAll('[data-nested-product-help]').forEach(initializeHelpTooltip);

    const form = root.querySelector('[data-nested-product-form]');
    const configNode = root.querySelector('[data-nested-product-config]');
    const assemblyInputs = [...root.querySelectorAll('[data-nested-product-assembly]')];
    const rpiSelect = root.querySelector('[data-nested-product-rpi]');
    const byoRpiOption = root.querySelector('[data-nested-product-rpi-byo]');
    const selectedRpiLabel = root.querySelector('[data-nested-product-rpi-selected-label]');
    const selectedRpiPrice = root.querySelector('[data-nested-product-rpi-selected-price]');
    const upgradeInputs = [...root.querySelectorAll('[data-nested-product-upgrade]')];
    const cameraInput = root.querySelector('[data-nested-product-camera]');
    const cameraChoice = root.querySelector('[data-nested-product-camera-choice]');
    const cameraPrice = root.querySelector('[data-nested-product-camera-price]');
    const quantityInput = root.querySelector('[data-nested-product-quantity]');
    const quantityActions = [...root.querySelectorAll('[data-nested-product-quantity-action]')];
    const submitButton = root.querySelector('[data-nested-product-submit]');
    const status = root.querySelector('[data-nested-product-status]');

    if (!form || !configNode || assemblyInputs.length !== 2 || !rpiSelect || rpiSelect.options.length !== 5 || !byoRpiOption || !selectedRpiLabel || !selectedRpiPrice || upgradeInputs.length !== 4 || !cameraInput || !cameraChoice || !cameraPrice || !quantityInput || quantityActions.length !== 2 || !submitButton || !status ) return;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      status.textContent = 'The product configuration could not be read. Reload the page and try again.';
      console.error('[Nested product] Invalid Liquid configuration', error);
      return;
    }

    if (!config.configured) return;

    let submitting = false;
    [...assemblyInputs, ...upgradeInputs].forEach((input) => {
      if (input.disabled) input.checked = false;
    });
    form.addEventListener('change', updateConfiguration);
    quantityInput.addEventListener('input', updateSummary);
    quantityActions.forEach((action) => {
      action.addEventListener('click', adjustQuantity);
      action.addEventListener('keydown', handleQuantityActionKeydown);
    });
    form.addEventListener('submit', addConfiguredProduct);
    updateRaspberryPiAndCameraAvailability();
    updateSummary();

    function adjustQuantity(event) {
      const direction = event.currentTarget.dataset.nestedProductQuantityAction === 'increment' ? 1 : -1;
      const quantity = validQuantity() || 1;
      quantityInput.value = Math.max(1, quantity + direction);
      quantityInput.dispatchEvent(new Event('input', {bubbles: true}));
    }

    function handleQuantityActionKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      adjustQuantity(event);
    }

    function selectedComponentKeys() {
      const selectedRpi = rpiSelect.selectedOptions[0];
      return [
        ...(selectedRpi?.dataset.componentKey ? [selectedRpi.dataset.componentKey] : []),
        ...upgradeInputs.filter((input) => input.checked).map((input) => input.dataset.componentKey),
      ];
    }

    function selectedAssembly() {
      const selectedInput = assemblyInputs.find((input) => input.checked);
      return selectedInput ? config.assemblies[selectedInput.value] : null;
    }

    function updateConfiguration(event) {
      if (event.target.matches('[data-nested-product-assembly], [data-nested-product-rpi]')) {
        updateRaspberryPiAndCameraAvailability();
      }
      updateSummary();
      if (!event.target.matches('[data-nested-product-assembly]')) return;

      const assembly = selectedAssembly();
      if (!assembly) return;

      updateVariantUrl(assembly.variantId);
      if (assembly.featuredMediaId) {
        root.dispatchEvent(new CustomEvent('nested-product:assembly-change', {
          bubbles: true,
          detail: {featuredMediaId: assembly.featuredMediaId},
        }));
      }
    }

    function updateRaspberryPiAndCameraAvailability() {
      const byoUnavailable = assemblyInputs.find((input) => input.checked)?.value === 'preassembled';
      byoRpiOption.disabled = byoUnavailable;

      if (!rpiSelect.selectedOptions[0] || rpiSelect.selectedOptions[0].disabled) {
        const availableRpiOption = [...rpiSelect.options].find((option) => !option.disabled);
        if (availableRpiOption) {
          rpiSelect.value = availableRpiOption.value;
        } else {
          rpiSelect.selectedIndex = -1;
        }
      }

      const selectedRpiOption = rpiSelect.value ? rpiSelect.selectedOptions[0] : null;
      selectedRpiLabel.textContent = selectedRpiOption?.dataset.selectionLabel || 'No Raspberry Pi models available';
      selectedRpiPrice.textContent = selectedRpiOption?.dataset.selectionPrice || '';
      const cameraComponentKey = selectedRpiOption?.dataset.cameraComponentKey;
      const cameraComponent = config.components[cameraComponentKey];
      const cameraAvailable = Boolean(cameraComponent?.available && number(cameraComponent.variantId));
      cameraInput.dataset.componentKey = cameraComponentKey;
      cameraInput.disabled = !cameraAvailable;
      if (!cameraAvailable) cameraInput.checked = false;
      cameraChoice.classList.toggle('nested-product__choice--unavailable', !cameraAvailable);
      cameraPrice.textContent = cameraAvailable ? `+${formatMoney(cameraComponent.price)}` : 'Unavailable';
    }

    function updateSummary() {
      const selectedKeys = selectedComponentKeys();
      const assembly = selectedAssembly();
      const selectedAssemblyInput = assemblyInputs.find((input) => input.checked);
      const selectedRpiOption = rpiSelect.selectedOptions[0];
      const selectedRpiComponent = config.components[selectedRpiOption?.dataset.componentKey];
      const selectedRpiIsAvailable = Boolean(selectedRpiComponent?.available && number(selectedRpiComponent.variantId));
      if (!assembly || (selectedAssemblyInput?.value === 'preassembled' && !selectedRpiIsAvailable)) {
        root.removeAttribute('data-price-ready');
        return;
      }
      root.querySelectorAll('[data-nested-product-summary]').forEach((row) => {
        row.hidden = !selectedKeys.includes(row.dataset.nestedProductSummary);
      });

      const quantity = validQuantity() || 1;
      const unitTotal = selectedKeys.reduce(
        (total, key) => total + number(config.components[key]?.price),
        number(assembly?.price),
      );
      const total = unitTotal * quantity;

      root.querySelectorAll('[data-nested-product-unit-total]').forEach((element) => {
        element.textContent = formatMoney(unitTotal);
      });
      root.querySelectorAll('[data-nested-product-total]').forEach((element) => {
        element.textContent = formatMoney(total);
      });
      root.querySelectorAll('[data-nested-product-sku]').forEach((element) => {
        element.textContent = assembly?.sku || '';
      });
      const totalLabel = root.querySelector('[data-nested-product-total-label]');
      if (totalLabel) totalLabel.textContent = quantity === 1 ? 'Total' : `Total for ${quantity}`;
      root.dataset.priceReady = 'true';

      const includesList = root.querySelector('#includes_list');
      if (!includesList) return;

      const includedAccessories = [
        {
          selected: Boolean(selectedRpiOption?.dataset.componentKey),
          key: 'raspberry-pi',
          label: selectedRpiOption?.dataset.selectionLabel,
        },
        {
          selected: selectedKeys.includes('oled'),
          key: 'oled-display',
          label: 'Pioreactor OLED display',
          path: 'products/pioreactor-oled-display',
        },
        {
          selected: selectedKeys.includes('camera-8210') || selectedKeys.includes('camera-8211'),
          key: 'camera',
          label: 'Pioreactor onboard camera',
          path: 'products/camera-kit',
        },
        {
          selected: selectedKeys.includes('xr'),
          key: 'xr',
          label: config.productHandle === 'pioreactor-20ml'
            ? 'Pioreactor 20ml v1.5 XR upgrade'
            : 'Pioreactor 40ml v1.5 XR upgrade',
          path: config.productHandle === 'pioreactor-20ml'
            ? 'products/pioreactor-20ml-v1-5-xr-upgrade-kit'
            : 'products/pioreactor-40ml-v1-5-xr-upgrade-kit',
        },
        {
          selected: selectedKeys.includes('precision-temperature'),
          key: 'precision-temperature',
          label: 'Precision temperature upgrade',
          path: 'products/precision-temperature-sensor',
        },
      ];

      includedAccessories.forEach((accessory) => {
        let item = includesList.querySelector(`[data-included-accessory="${accessory.key}"]`);

        if (!accessory.selected) {
          item?.remove();
          return;
        }
        if (item) {
          if (!accessory.path && item.textContent !== `${accessory.label}.`) {
            item.textContent = `${accessory.label}.`;
          }
          return;
        }

        item = document.createElement('li');
        item.dataset.includedAccessory = accessory.key;

        if (accessory.path) {
          const link = document.createElement('a');
          link.href = localeAwarePath(accessory.path);
          link.textContent = accessory.label;

          item.append(link, '.');
        } else {
          item.textContent = `${accessory.label}.`;
        }
        includesList.appendChild(item);
      });
    }

    async function addConfiguredProduct(event) {
      event.preventDefault();
      if (submitting) return;

      const quantity = validQuantity();
      if (!quantity) {
        quantityInput.reportValidity();
        status.textContent = 'Enter a whole-number quantity of at least 1.';
        return;
      }

      const selectedKeys = selectedComponentKeys();
      const assembly = selectedAssembly();
      const parentVariantId = number(assembly?.variantId);
      if (!assembly?.available || !parentVariantId) {
        status.textContent = 'That assembly option is not available. Choose another assembly option and try again.';
        return;
      }
      if (assemblyInputs.find((input) => input.checked)?.value === 'preassembled' && !rpiSelect.selectedOptions[0]?.dataset.componentKey) {
        status.textContent = 'No Raspberry Pi models are currently available for a pre-assembled unit. Choose Kit instead.';
        return;
      }
      const unavailableComponentKey = selectedKeys.find((key) => {
        const component = config.components[key];
        return !component?.available || !number(component.variantId);
      });
      if (unavailableComponentKey) {
        status.textContent = 'That option is not available. Choose another configuration and try again.';
        return;
      }
      const items = [
        {
          id: parentVariantId,
          quantity,
          properties: configurationProperties(selectedKeys),
        },
        ...selectedKeys.map((key) => ({
          id: number(config.components[key].variantId),
          quantity,
          parent_id: parentVariantId,
        })),
      ];

      submitting = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      status.textContent = 'Adding to cart...';
      form.classList.add('loading');

      try {
        const response = await fetch(localeAwarePath('cart/add.js'), {
          method: 'POST',
          headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
          body: JSON.stringify({items}),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Shopify rejected the cart request.');
        }
        console.info('[Nested product] cart/add.js response', result);

        const nestedChildren = Array.isArray(result.items)
          ? result.items.filter((item) => item.parent_relationship?.parent_key)
          : [];
        if (nestedChildren.length !== selectedKeys.length) {
          const error = new Error('Shopify added the items but did not confirm every add-on as a nested child.');
          error.cartMayHaveChanged = true;
          console.error('[Nested product] Unexpected nested cart response', {items, result});
          throw error;
        }

        const headerCartCount = document.querySelector('.header-cart-count');
        if (headerCartCount) {
          headerCartCount.textContent = number(headerCartCount.textContent) + quantity;
          headerCartCount.classList.add('active');
        }

        const cartLink = document.createElement('a');
        cartLink.href = localeAwarePath('cart');
        cartLink.textContent = 'View cart';
        status.replaceChildren(document.createTextNode('Added to cart. '), cartLink);

        submitting = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        form.classList.remove('loading');
      } catch (error) {
        const reason = error instanceof Error ? error.message : 'The cart request failed.';
        const cartMayHaveChanged = Boolean(error.cartMayHaveChanged);
        status.textContent = `${reason} ${cartMayHaveChanged ? 'Inspect the cart before trying again.' : 'Check your selections and try again.'}`;
        submitting = cartMayHaveChanged;
        submitButton.disabled = cartMayHaveChanged;
        submitButton.removeAttribute('aria-busy');
        form.classList.remove('loading');
        console.error('[Nested product] Add to cart failed', error);
      }
    }

    function configurationProperties(selectedKeys) {
      let raspberryPi = 'Bring your own Pi';
      if (selectedKeys.includes('rpi')) raspberryPi = 'Raspberry Zero 2';
      if (selectedKeys.includes('pi3')) raspberryPi = 'Raspberry Pi 3 B';
      if (selectedKeys.includes('pi4')) raspberryPi = 'Raspberry Pi 4 B';
      if (selectedKeys.includes('pi5')) raspberryPi = 'Raspberry Pi 5';

      const configuration = [
        raspberryPi,
        ...(selectedKeys.includes('oled') ? ['OLED'] : []),
        ...(selectedKeys.includes('camera-8210') || selectedKeys.includes('camera-8211') ? ['Camera'] : []),
        ...(selectedKeys.includes('xr') ? ['XR'] : []),
        ...(selectedKeys.includes('precision-temperature') ? ['Precision temperature'] : []),
      ].join(' · ');

      return {
        Configuration: configuration,
        _pioreactor_nested_product: 'v1',
      };
    }

    function validQuantity() {
      const quantity = Number(quantityInput.value);
      return Number.isInteger(quantity) && quantity >= 1 ? quantity : null;
    }

    function updateVariantUrl(variantId) {
      if (!window.history?.replaceState || !variantId || window.Shopify?.preview_host) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({path: url.toString()}, '', url.toString());
    }

    function formatMoney(minorUnits) {
      if (window.Shopify?.formatMoney && window.Theme?.moneyFormat) {
        return window.Shopify.formatMoney(minorUnits, window.Theme.moneyFormat);
      }
      if (window.Theme?.moneyFormat) {
        return formatThemeMoney(minorUnits, window.Theme.moneyFormat);
      }
      return new Intl.NumberFormat(config.locale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: config.currency,
      }).format(minorUnits / 100);
    }
  }

  function number(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatThemeMoney(minorUnits, format) {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const formatType = format.match(placeholder)?.[1] || 'amount';
    const formats = {
      amount: [2, '.', ','],
      amount_no_decimals: [0, '.', ','],
      amount_with_comma_separator: [2, ',', '.'],
      amount_no_decimals_with_comma_separator: [0, ',', '.'],
      amount_with_apostrophe_separator: [2, '.', "'"],
    };
    const [precision, decimal, thousands] = formats[formatType] || formats.amount;
    const fixed = (number(minorUnits) / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = fraction ? `${grouped}${decimal}${fraction}` : grouped;
    return format.replace(placeholder, value);
  }

  function localeAwarePath(path) {
    const root = window.Shopify?.routes?.root || window.Theme?.routes?.root_url || '/';
    return `${root.endsWith('/') ? root : `${root}/`}${path}`;
  }

  function initializeAllNestedProducts() {
    document.querySelectorAll('[data-nested-product]').forEach(initializeNestedProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllNestedProducts);
  } else {
    initializeAllNestedProducts();
  }
})();
